import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { X, StickyNote, AlertTriangle, Home } from "lucide-react";
import toast from "react-hot-toast";
import moment from "moment";
import {
  updateLead, getLeadReminders, createLeadReminders, deleteLeadReminder,
} from "../../../api/lead";
import AppraisalReminderSeries from "./AppraisalReminderSeries";
import AgentNotesSummary from "../../../components/notes/AgentNotesSummary";
import {
  useLinkedNotes,
  LinkedNotesSection,
  linkedToSummaryNotes,
} from "../../../components/notes/linkedNotes";

/**
 * Notes on a lead, while the lead is still just a lead.
 *
 * Christian Gravias, 21 Aug 2026: "Once the leads have entered the agents
 * dashboard (after CG has assigned them) there's no section to write notes until
 * they've been added to the database or appraisal."
 *
 * That was the gap. A seller lead lands from Meta / REA / Domain, Christian hands
 * it to an agent, and the agent rings them, but there was nowhere to record what
 * was said until the lead had been converted into an appraisal or filed into the
 * Database, which is exactly the point at which the agent needs the call history
 * to decide which of those two it should be. So the notes have to live on the LEAD.
 *
 * Written to Lead.agent_notes, the append-only [{timestamp, author, content}]
 * thread the Buyer Enquiries modal already uses, so a note written here shows up
 * there too and the backend keeps mirroring it into the person's ContactNote
 * thread once they have a Contact record (lead_detail_views.py).
 *
 * Append-only on purpose: notes are "hard marked in with a date & who entered it
 * in" (Monday 12838023223), the same rule as buyer notes. There is no edit and no
 * delete, because an overwritable notes box is what lost the notes last time.
 *
 * Christian Gravias, 24 Aug 2026: "even they save the notes it disappear."
 *
 * That was a SECOND, separate way of losing a note, and a worse one, because it
 * ate notes that had already been saved. This modal used to PUT the whole
 * agent_notes array, built from the snapshot it took when it was opened. Christian
 * hands leads over all day and that appends "Sent to <Agent>: ..." to the same
 * array, so if he handed the lead over while the agent had the thread open, the
 * agent's save wrote back its stale array and deleted Christian's note - and the
 * other way round too. Now only the NEW note goes up, as append_agent_note, and
 * the backend appends it to whatever is actually in the database
 * (lead_detail_views._append_agent_note). The response carries the real thread
 * back, so what is on screen after a save is the database, not a local guess.
 *
 * Christian Gravias, 24 Aug 2026: "Adriano has typed long notes and keeps losing
 * them. Please make it so you have to click the X at the top right corner to exit
 * the note as we keep losing what we type."
 *
 * So this modal does NOT close on a background click any more. That was the whole
 * bug: the overlay called onClose() on any click landing outside the white card,
 * and a note long enough to be worth writing takes long enough to type that a
 * stray click off the edge of the textarea threw the lot away with no warning.
 * Closing is a deliberate act on the X or on Close now, and even those will not
 * silently bin a half-written note - see requestClose below. Escape is not wired
 * up here and must not be.
 *
 * Simon Abbott, 30 Sep 2026 (Monday 13166757620, CRM-2026-00083): "Cant set a
 * reminder series in the holding area. Only notes. When adding a note you need
 * to be able to add a reminder series too."
 *
 * An appraisal has had a reminder series under its note since September, but a
 * lead that is still in the holding area had only this thread, so "called, no
 * answer, try again Thursday" could be written down and Thursday's call could
 * not be booked. The same series picker now sits under the note box here and
 * the one Save button saves either half on its own or both together. The
 * reminders go up separately from the note (createLeadReminders, never a PUT
 * on the lead) and second, so a failed booking cannot lose a note that has
 * already saved. The backend puts each one on the person's Contact where there
 * is one, so it lands in Who-to-Call and on the calendar with every other
 * call-back, and on a task tied to the lead where there is not.
 */

// The display stamp the existing thread is written with (LeadsPage onAddNote).
// Kept byte-identical so notes written here render the same in the Buyer
// Enquiries modal, which prints note.timestamp straight out.
const STAMP_FORMAT = "D/M/YYYY h:mm a";

const normalise = (agentNotes) => (Array.isArray(agentNotes) ? agentNotes : []);

// Older notes only carry the display stamp; newer ones also carry an ISO
// created_at, which is the only one that sorts and reformats reliably.
const stampFor = (note) => {
  if (note?.created_at) {
    const parsed = moment(note.created_at);
    if (parsed.isValid()) return parsed.format("D MMM YYYY, h:mm a");
  }
  return note?.timestamp || "";
};

const LeadNotesModal = ({ isOpen, lead, onClose, onSaved }) => {
  const { user } = useSelector((state) => state.auth || {});
  const [draft, setDraft] = useState("");
  const [notes, setNotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Call-backs staged under the note, booked with the same Save button.
  const [reminders, setReminders] = useState([]);
  // Bumped after a booking so the series re-reads its "Already booked" list.
  const [reminderKey, setReminderKey] = useState(0);

  // Seeded when the thread is opened for a lead, and deliberately NOT re-seeded
  // on every change to lead.agent_notes: the parent list refreshes on a timer, and
  // re-seeding would wipe a half-typed note out from under the agent.
  useEffect(() => {
    if (!isOpen) return;
    setNotes(normalise(lead?.agent_notes));
    setDraft("");
    setReminders([]);
    setConfirmDiscard(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lead?.id]);

  // Newest first. The agent wants the last thing that happened, not the first.
  const thread = useMemo(() => [...notes].reverse(), [notes]);

  // The series reads and drops booked call-backs through the LEAD's endpoint.
  // Memoised on the lead id: the series reloads whenever these change, so a
  // fresh closure every render would have it fetching in a loop.
  const leadId = lead?.id;
  const fetchBooked = useCallback(
    () => getLeadReminders(leadId),
    [leadId]
  );
  const deleteBooked = useCallback(
    (kind, id) => deleteLeadReminder(leadId, kind, id),
    [leadId]
  );

  // Notes about this seller typed elsewhere - the appraisal, the contact, a
  // Database row - so the lead shows the whole history (CRM-2026-00106).
  const linked = useLinkedNotes(
    isOpen && leadId ? { lead: leadId, from: "seller_lead" } : {},
    notes.length
  );

  if (!isOpen || !lead) return null;

  const leadName = lead.name || lead.full_name || "No Name";
  const address = lead.property_address || lead.owner_subject_address || "";
  // The CRM property this lead is linked to, if any (owner leads auto-create a
  // property profile). The FK serializes as a bare id, but handle a nested object
  // too; hidden when there's no link. Lets the agent jump straight to the listing
  // from the notes (DOMA 13343247771).
  const propertyId =
    (lead.property && typeof lead.property === "object" ? lead.property.id : lead.property) ||
    lead.property_id ||
    null;
  // What the person actually said when they enquired. Read-only, because it is
  // their words and not the agent's, so it sits above the thread not in it.
  const enquiryMessage = (lead.initial_notes || "").trim();

  // Nothing typed, so closing costs nothing. Typed and not saved, and the first
  // click on the X or on Close asks instead of closing - one more click to throw
  // the note away, which is one more than the background click ever gave anybody.
  // A staged reminder counts too: it is not booked until Save.
  const hasUnsavedDraft = draft.trim().length > 0 || reminders.length > 0;
  const canSave = draft.trim().length > 0 || reminders.length > 0;
  let saveLabel = "Save Note";
  if (saving) saveLabel = "Saving...";
  else if (!draft.trim() && reminders.length > 0) saveLabel = "Save Reminders";
  else if (reminders.length > 0) saveLabel = "Save Note & Reminders";
  const requestClose = () => {
    if (hasUnsavedDraft) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  // Books the staged series. Returns how many were booked; never throws, so a
  // note that has already saved is never reported as failed because of it.
  const bookReminders = async (content) => {
    if (reminders.length === 0) return 0;
    try {
      await createLeadReminders(
        lead.id,
        reminders.map((entry) => ({
          date: entry.date.toISOString(),
          reason: entry.reason,
        })),
        content
      );
      const booked = reminders.length;
      setReminders([]);
      setReminderKey((k) => k + 1);
      return booked;
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          (content
            ? "Note saved, but the reminders could not be booked."
            : "Failed to book the reminders.")
      );
      return 0;
    }
  };

  const plural = (n) => `${n} reminder${n === 1 ? "" : "s"}`;

  const handleSave = async () => {
    const content = draft.trim();
    if ((!content && reminders.length === 0) || saving) return;
    setSaving(true);
    try {
      if (!content) {
        // Reminders only: nothing to append to the thread.
        const booked = await bookReminders("");
        if (booked) toast.success(`${plural(booked)} booked.`);
        return;
      }

      const entry = {
        timestamp: moment().format(STAMP_FORMAT),
        created_at: moment().toISOString(),
        author: user?.name || user?.email || "Agent",
        content,
      };
      // Send ONLY the new note. Sending the whole array is what deleted notes
      // somebody else had already saved - see the note at the top of this file.
      const res = await updateLead(lead.id, { append_agent_note: entry });
      // The backend answers with the thread as stored, which includes anything
      // written while this modal was open. Fall back to a local append only if an
      // older backend is deployed and does not send it back.
      const saved = Array.isArray(res?.data?.agent_notes)
        ? res.data.agent_notes
        : [...notes, entry];
      setNotes(saved);
      setDraft("");
      // The note is safe; now the call-backs, carrying the note as their reason.
      const booked = await bookReminders(content);
      toast.success(booked ? `Note added and ${plural(booked)} booked.` : "Note added.");
      // Hand the whole lead back so the caller's row can show the new count and
      // the latest note without a reload.
      // A note that says "called" marks the lead called on the server, so the
      // call time rides back too and a New card can move to Connected.
      onSaved &&
        onSaved({
          ...lead,
          agent_notes: saved,
          ...(res?.data?.last_contact_date ? { last_contact_date: res.data.last_contact_date } : {}),
        });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save the note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/40 p-3 overflow-y-auto"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg my-6 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#2D3441] flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-[#55C5D0]" />
            Agent Notes
          </h2>
          <button
            type="button"
            onClick={requestClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close notes"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600">
            <div className="font-bold text-gray-900 text-sm">{leadName}</div>
            <div className="mt-0.5">
              {[lead.phone, lead.email].filter(Boolean).join(" · ") || "No contact details"}
            </div>
            {address && <div className="mt-0.5">{address}</div>}
            {/* Opens the linked listing in a NEW tab on purpose: in-app nav would
                unmount this modal and bin a half-typed note (DOMA 13343247771). */}
            {propertyId && (
              <a
                href={`/properties/${propertyId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-[#55C5D0] text-[#128f89] px-2.5 py-1 text-xs font-bold hover:bg-[#55C5D0]/10"
              >
                <Home className="w-3.5 h-3.5" /> View Property
              </a>
            )}
          </div>

          {enquiryMessage && (
            <div>
              <span className="block text-xs font-semibold text-gray-500 mb-1">
                What they sent through
              </span>
              {/* Styled as a quote, not a bordered box: a box here reads as an
                  editable field, and this is the enquirer's words. */}
              <p className="text-sm text-gray-700 whitespace-pre-wrap border-l-2 border-[#55C5D0] bg-gray-50 rounded-r-lg px-3 py-2 italic">
                {enquiryMessage}
              </p>
            </div>
          )}

          <div>
            <label
              className="block text-xs font-semibold text-gray-500 mb-1"
              htmlFor="lead-note-draft"
            >
              Add a note
            </label>
            <textarea
              id="lead-note-draft"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setConfirmDiscard(false);
              }}
              rows={4}
              placeholder="Called, left a message. Wants a price before Christmas."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
            />
            {/* The call-back series, directly under the note and booked by the
                same button (Simon Abbott, Monday 13166757620). */}
            <div className="mt-2">
              <AppraisalReminderSeries
                key={`${lead.id}-${reminderKey}`}
                fetchBooked={fetchBooked}
                deleteBooked={deleteBooked}
                value={reminders}
                onChange={(next) => {
                  setReminders(next);
                  setConfirmDiscard(false);
                }}
                pendingLabel="Saving with this note"
              />
            </div>
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave || saving}
                className="bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold text-sm"
              >
                {saveLabel}
              </button>
            </div>
          </div>

          <div>
            <span className="block text-xs font-semibold text-gray-500 mb-2">
              Agent Notes ({notes.length})
            </span>
            {/* AI summary of the notes below (Monday 13202770775) */}
            <AgentNotesSummary
              notes={[...thread, ...linkedToSummaryNotes(linked)]}
              subject={[leadName, address].filter(Boolean).join(", ")} className="mb-2" />
            {thread.length === 0 ? (
              linked.length ? null : <p className="text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-4 text-center">
                No notes yet.
              </p>
            ) : (
              <div className="space-y-2">
                {thread.map((note, i) => (
                  <div
                    key={`${note.created_at || note.timestamp || "note"}-${i}`}
                    className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
                  >
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                    <div className="mt-1 text-[11px] font-semibold text-gray-400">
                      {[
                        note.author || "Agent",
                        stampFor(note),
                        // Copied from the Database row's call notes (CRM-2026-00105)
                        note.source === "database" ? "From Database" : "",
                      ].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <LinkedNotesSection notes={linked} className="mt-3" />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100">
          {confirmDiscard ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-xs font-semibold text-amber-700 flex items-start gap-1.5">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-px" />
                {draft.trim()
                  ? "You have not saved this note yet."
                  : "You have not booked these reminders yet."}
              </p>
              <div className="flex items-center justify-end gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setConfirmDiscard(false)}
                  className="bg-[#55C5D0] hover:bg-[#45b5c0] text-white px-3 py-2 rounded-lg font-bold text-xs"
                >
                  Keep Writing
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700"
                >
                  {draft.trim() ? "Discard Note" : "Discard Reminders"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={requestClose}
                className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadNotesModal;
